import { useEffect, useRef, useState } from "react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PinConfirmDialog } from "@/components/common/PinPad";
import { Upload, Loader2, Plus, Trash2, Store, Users, Palette, Star, MessageSquare, AlertTriangle } from "lucide-react";
import ConnectPanel from "@/components/dashboard/ConnectPanel";
import VatRatesSettings from "@/components/dashboard/VatRatesSettings";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-stone-400">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export default function SettingsPanel({ restaurant, slug, restaurants, soundOn, setSoundOn, onBrandUpdated, onRestaurantsChanged }) {
  const [brand, setBrand] = useState({
    name: restaurant.name || "", tagline: restaurant.tagline || "",
    brand_primary: restaurant.brand_primary || "#123026",
    brand_secondary: restaurant.brand_secondary || "#C69C4A",
    logo_letter: restaurant.logo_letter || "N",
  });
  const [savingBrand, setSavingBrand] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoRef = useRef(null);
  const [showCreateResto, setShowCreateResto] = useState(false);
  const [reviewCfg, setReviewCfg] = useState({
    google_review_url: restaurant.google_review_url || "",
    reviews_enabled: restaurant.reviews_enabled !== false,
    closing_message: restaurant.closing_message || "",
    closing_google_enabled: restaurant.closing_google_enabled !== false,
  });
  const [savingReview, setSavingReview] = useState(false);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [showReset, setShowReset] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetPinOpen, setResetPinOpen] = useState(false);

  const doReset = async (authorizedBy, pin) => {
    setResetPinOpen(false);
    setResetting(true);
    try {
      const res = await api.resetActivity(slug, resetConfirm.trim(), pin);
      const n = Object.values(res.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Données d'activité réinitialisées (${n} éléments supprimés)${authorizedBy ? ` · autorisé par ${authorizedBy}` : ""}`);
      setShowReset(false); setResetConfirm("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setResetting(false); }
  };

  useEffect(() => {
    setReviewCfg({
      google_review_url: restaurant.google_review_url || "",
      reviews_enabled: restaurant.reviews_enabled !== false,
      closing_message: restaurant.closing_message || "",
      closing_google_enabled: restaurant.closing_google_enabled !== false,
    });
  }, [restaurant.slug, restaurant.google_review_url, restaurant.reviews_enabled, restaurant.closing_message, restaurant.closing_google_enabled]);

  useEffect(() => { api.reviewsSummary(slug).then(setReviewSummary).catch(() => setReviewSummary(null)); }, [slug]);

  const saveReview = async () => {
    setSavingReview(true);
    try {
      const url = reviewCfg.google_review_url.trim();
      if (url && !/^https?:\/\//i.test(url)) { toast.error("L'URL Google doit commencer par http:// ou https://"); setSavingReview(false); return; }
      const r = await api.updateReviewSettings(slug, { google_review_url: url || null, reviews_enabled: reviewCfg.reviews_enabled, closing_message: reviewCfg.closing_message ?? "", closing_google_enabled: reviewCfg.closing_google_enabled });
      onBrandUpdated({ ...restaurant, ...r });
      toast.success("Réglages des avis enregistrés");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingReview(false); }
  };

  useEffect(() => {
    setBrand({
      name: restaurant.name || "", tagline: restaurant.tagline || "",
      brand_primary: restaurant.brand_primary || "#123026",
      brand_secondary: restaurant.brand_secondary || "#C69C4A",
      logo_letter: restaurant.logo_letter || "N",
    });
  }, [restaurant.slug]);

  const saveBrand = async () => {
    setSavingBrand(true);
    try {
      const r = await api.updateRestaurant(slug, brand);
      onBrandUpdated(r);
      toast.success("Identité mise à jour");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingBrand(false); }
  };

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const res = await api.uploadLogo(slug, file);
      onBrandUpdated(res.restaurant);
      toast.success("Logo mis à jour");
    } catch (e) { toast.error("Échec de l'upload"); }
    finally { setUploadingLogo(false); }
  };

  const addStaff = null;
  const removeStaff = null;

  return (
    <div className="max-w-2xl space-y-4">
      {/* Stripe Connect payments */}
      <ConnectPanel slug={slug} />

      {/* Brand */}
      <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-5 space-y-4">
        <h3 className="font-serif-display text-lg flex items-center gap-2"><Palette className="w-4 h-4 text-[#C69C4A]" /> Identité de la marque</h3>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl overflow-hidden bg-[#0A1712] grid place-items-center shrink-0 border border-[#1B3028]">
            {restaurant.logo_image ? (
              <img src={fileUrl(restaurant.logo_image)} alt="logo" className="w-full h-full object-cover" />
            ) : (
              <span className="font-serif-display text-2xl text-[#C69C4A]">{brand.logo_letter}</span>
            )}
          </div>
          <div>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" data-testid="logo-file-input" onChange={handleLogo} />
            <Button variant="outline" onClick={() => logoRef.current?.click()} disabled={uploadingLogo} className="rounded-full bg-transparent border-[#1B3028] text-stone-200 hover:bg-[#1A3D32]">
              {uploadingLogo ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />} Importer un logo
            </Button>
            <p className="text-[11px] text-stone-500 mt-1.5">Sinon, l'initiale ci-dessous est utilisée.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nom"><Input data-testid="brand-name" value={brand.name} onChange={(e) => setBrand({ ...brand, name: e.target.value })} className="bg-[#0A1712] border-[#1B3028] text-stone-100 rounded-xl" /></Field>
          <Field label="Initiale du logo"><Input data-testid="brand-letter" maxLength={2} value={brand.logo_letter} onChange={(e) => setBrand({ ...brand, logo_letter: e.target.value })} className="bg-[#0A1712] border-[#1B3028] text-stone-100 rounded-xl" /></Field>
        </div>
        <Field label="Slogan"><Input data-testid="brand-tagline" value={brand.tagline} onChange={(e) => setBrand({ ...brand, tagline: e.target.value })} className="bg-[#0A1712] border-[#1B3028] text-stone-100 rounded-xl" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Couleur principale">
            <div className="flex items-center gap-2">
              <input type="color" data-testid="brand-primary" value={brand.brand_primary} onChange={(e) => setBrand({ ...brand, brand_primary: e.target.value })} className="w-10 h-10 rounded-lg bg-transparent border border-[#1B3028] cursor-pointer" />
              <Input value={brand.brand_primary} onChange={(e) => setBrand({ ...brand, brand_primary: e.target.value })} className="bg-[#0A1712] border-[#1B3028] text-stone-100 rounded-xl" />
            </div>
          </Field>
          <Field label="Couleur secondaire (or)">
            <div className="flex items-center gap-2">
              <input type="color" data-testid="brand-secondary" value={brand.brand_secondary} onChange={(e) => setBrand({ ...brand, brand_secondary: e.target.value })} className="w-10 h-10 rounded-lg bg-transparent border border-[#1B3028] cursor-pointer" />
              <Input value={brand.brand_secondary} onChange={(e) => setBrand({ ...brand, brand_secondary: e.target.value })} className="bg-[#0A1712] border-[#1B3028] text-stone-100 rounded-xl" />
            </div>
          </Field>
        </div>
        <Button data-testid="brand-save" onClick={saveBrand} disabled={savingBrand} className="rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-semibold">
          {savingBrand ? "..." : "Enregistrer l'identité"}
        </Button>
      </div>

      {/* Restaurants */}
      <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-5 space-y-4">
        <h3 className="font-serif-display text-lg flex items-center gap-2"><Store className="w-4 h-4 text-[#C69C4A]" /> Mes restaurants</h3>
        <div className="space-y-2">
          {restaurants.map((r) => (
            <div key={r.slug} className="flex items-center justify-between rounded-xl bg-[#0A1712] border border-[#1B3028] px-3 py-2">
              <span className="text-sm text-stone-100">{r.name}</span>
              <span className="text-xs text-stone-500">{r.slug}</span>
            </div>
          ))}
        </div>
        <Button data-testid="create-restaurant-open" onClick={() => setShowCreateResto(true)} className="rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> Créer un restaurant
        </Button>
      </div>

      {/* Customer reviews */}
      <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-5 space-y-4">
        <h3 className="font-serif-display text-lg flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#C69C4A]" /> Avis clients</h3>
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium text-stone-100">Demander un avis</p><p className="text-xs text-stone-500">Proposé au client après paiement et sur le suivi</p></div>
          <Switch data-testid="reviews-enabled-toggle" checked={reviewCfg.reviews_enabled} onCheckedChange={(v) => setReviewCfg({ ...reviewCfg, reviews_enabled: v })} className="data-[state=checked]:bg-emerald-600" />
        </div>
        <Field label="Lien Google (fiche établissement)">
          <Input data-testid="google-review-url" value={reviewCfg.google_review_url} onChange={(e) => setReviewCfg({ ...reviewCfg, google_review_url: e.target.value })} placeholder="https://g.page/r/..." className="bg-[#0A1712] border-[#1B3028] text-stone-100 rounded-xl" />
        </Field>
        <p className="text-[11px] text-stone-500 -mt-1">Le bouton « Laisser un avis sur Google » redirige vers ce lien, quelle que soit la note. Aucun filtrage.</p>
        <Field label="Message de remerciement après clôture de la table">
          <textarea data-testid="closing-message" value={reviewCfg.closing_message} onChange={(e) => setReviewCfg({ ...reviewCfg, closing_message: e.target.value })} rows={3} maxLength={400} placeholder="Merci de votre visite ! Votre table a été clôturée. Nous espérons vous revoir très bientôt." className="w-full bg-[#0A1712] border border-[#1B3028] text-stone-100 rounded-xl px-3 py-2 text-sm resize-none" />
        </Field>
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium text-stone-100">Bouton « Donner mon avis sur Google » après clôture</p><p className="text-xs text-stone-500">Affiché sur la page de remerciement si le lien Google est renseigné</p></div>
          <Switch data-testid="closing-google-toggle" checked={reviewCfg.closing_google_enabled} onCheckedChange={(v) => setReviewCfg({ ...reviewCfg, closing_google_enabled: v })} className="data-[state=checked]:bg-emerald-600" />
        </div>
        <Button data-testid="reviews-save" onClick={saveReview} disabled={savingReview} className="rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-semibold">
          {savingReview ? "..." : "Enregistrer les avis"}
        </Button>

        {reviewSummary && reviewSummary.count > 0 && (
          <div data-testid="reviews-summary" className="pt-2 border-t border-[#1B3028] space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <Star className="w-5 h-5 text-[#C69C4A] fill-[#C69C4A]" />
                <span className="text-2xl font-bold text-stone-100">{reviewSummary.average?.toFixed(1)}</span>
              </div>
              <span className="text-xs text-stone-500">sur {reviewSummary.count} avis</span>
            </div>
            <div className="space-y-1">
              {[5, 4, 3, 2, 1].map((n) => {
                const c = reviewSummary.distribution?.[String(n)] || 0;
                const pct = reviewSummary.count ? Math.round((c / reviewSummary.count) * 100) : 0;
                return (
                  <div key={n} className="flex items-center gap-2 text-xs text-stone-400">
                    <span className="w-3">{n}</span><Star className="w-3 h-3 text-[#C69C4A]" />
                    <div className="flex-1 h-1.5 rounded-full bg-[#0A1712] overflow-hidden"><div className="h-full bg-[#C69C4A]" style={{ width: `${pct}%` }} /></div>
                    <span className="w-6 text-right">{c}</span>
                  </div>
                );
              })}
            </div>
            {reviewSummary.recent?.filter((r) => r.comment).length > 0 && (
              <div className="space-y-2 pt-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Derniers commentaires</p>
                {reviewSummary.recent.filter((r) => r.comment).slice(0, 5).map((r, i) => (
                  <div key={i} className="rounded-xl bg-[#0A1712] border border-[#1B3028] px-3 py-2">
                    <div className="flex gap-0.5 mb-1">{Array.from({ length: r.rating }).map((_, k) => <Star key={k} className="w-3 h-3 text-[#C69C4A] fill-[#C69C4A]" />)}</div>
                    <p className="text-sm text-stone-300">{r.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {reviewSummary && reviewSummary.count === 0 && (
          <p className="text-xs text-stone-600 pt-2 border-t border-[#1B3028]">Aucun avis pour l'instant.</p>
        )}
      </div>

      {/* Service options */}
      <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-5 space-y-4">
        <h3 className="font-serif-display text-lg">Options de service</h3>
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium text-stone-100">Suggestions (upsell)</p><p className="text-xs text-stone-500">Compléments proposés après un ajout</p></div>
          <Switch data-testid="settings-upsell-toggle" checked={restaurant.upsell_enabled} onCheckedChange={async (v) => { const r = await api.updateRestaurant(slug, { upsell_enabled: v }); onBrandUpdated(r); }} className="data-[state=checked]:bg-emerald-600" />
        </div>
        <div className="rounded-xl border border-[#1B3028] p-3 space-y-2">
          <p className="text-sm font-medium text-stone-100">Sécurité des QR clients</p>
          <p className="text-xs text-stone-500">Après clôture d'une table, les anciennes sessions sont invalidées. Choisissez comment un nouveau service s'ouvre.</p>
          <div className="flex flex-wrap gap-2 items-center">
            <select data-testid="settings-qr-mode" value={restaurant.qr_opening_mode || "auto"} onChange={async (e) => { try { const r = await api.updateRestaurant(slug, { qr_opening_mode: e.target.value }); onBrandUpdated(r); toast.success("Mode d'ouverture enregistré"); } catch { toast.error("Erreur d'enregistrement"); } }} className="bg-[#0C1713] border border-[#1B3028] rounded-lg px-2 py-1.5 text-sm text-stone-100">
              <option value="auto">Ouverture automatique au scan (verrou après clôture)</option>
              <option value="staff">Ouverture par le personnel uniquement</option>
            </select>
            {(restaurant.qr_opening_mode || "auto") === "auto" && (
              <label className="text-xs text-stone-400 flex items-center gap-1.5">Verrou après clôture
                <input data-testid="settings-qr-lock" type="number" min="0" max="240" defaultValue={restaurant.qr_lock_minutes ?? 15} onBlur={async (e) => { const v = Math.max(0, parseInt(e.target.value || "0", 10)); if (v === (restaurant.qr_lock_minutes ?? 15)) return; try { const r = await api.updateRestaurant(slug, { qr_lock_minutes: v }); onBrandUpdated(r); toast.success("Verrou enregistré"); } catch { toast.error("Erreur d'enregistrement"); } }} className="w-16 bg-[#0C1713] border border-[#1B3028] rounded-lg px-2 py-1 text-stone-100" /> min
              </label>
            )}
          </div>
          <input data-testid="settings-website" defaultValue={restaurant.website || ""} placeholder="Site web (affiché sur la page de fin de service) — https://…" onBlur={async (e) => { const v = e.target.value.trim(); if (v === (restaurant.website || "")) return; try { const r = await api.updateRestaurant(slug, { website: v }); onBrandUpdated(r); toast.success("Site web enregistré"); } catch (err) { toast.error(err?.response?.data?.detail || "Adresse invalide (http:// ou https://)"); } }} className="w-full bg-[#0C1713] border border-[#1B3028] rounded-lg px-2 py-1.5 text-sm text-stone-100" />
        </div>
        <div className="rounded-xl border border-[#1B3028] p-3 space-y-2" data-testid="settings-lock-block">
          <p className="text-sm font-medium text-stone-100">Verrouillage automatique du personnel</p>
          <p className="text-xs text-stone-500">Après inactivité, l'écran se verrouille et l'employé reprend sa session avec son PIN personnel. Le KDS continue de recevoir les tickets pendant le verrouillage.</p>
          <div className="flex flex-wrap gap-4">
            {[["lock_minutes_pos", "Tablettes serveur & Caisse", 5, 60], ["lock_minutes_kds", "Cuisine KDS", 10, 120]].map(([key, label, def, mx]) => (
              <label key={key} className="text-xs text-stone-400 flex items-center gap-1.5">{label}
                <input data-testid={`settings-${key}`} type="number" min="1" max={mx} defaultValue={restaurant[key] ?? def} onBlur={async (e) => { const v = Math.min(mx, Math.max(1, parseInt(e.target.value || String(def), 10))); if (v === (restaurant[key] ?? def)) return; try { const r = await api.updateRestaurant(slug, { [key]: v }); onBrandUpdated(r); toast.success("Verrouillage enregistré"); } catch { toast.error("Erreur d'enregistrement"); } }} className="w-16 bg-[#0C1713] border border-[#1B3028] rounded-lg px-2 py-1 text-stone-100" /> min
              </label>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium text-stone-100">Notification sonore</p><p className="text-xs text-stone-500">Bip à chaque nouvelle commande</p></div>
          <Switch checked={soundOn} onCheckedChange={setSoundOn} className="data-[state=checked]:bg-emerald-600" />
        </div>
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium text-stone-100">Alerte commandes prêtes</p><p className="text-xs text-stone-500">Seuil de relance : clignotement, son et relance auto (POS/salle)</p></div>
          <div className="flex items-center gap-2">
            <select data-testid="ready-alert-select"
              value={[0, 2, 3, 5].includes(restaurant.ready_alert_minutes ?? 3) ? String(restaurant.ready_alert_minutes ?? 3) : "custom"}
              onChange={async (e) => { const val = e.target.value; const v = val === "custom" ? 4 : parseInt(val); const r = await api.updateRestaurant(slug, { ready_alert_minutes: v }); onBrandUpdated(r); toast.success("Seuil d'alerte mis à jour"); }}
              className="bg-[#0A1712] border border-[#1B3028] rounded-xl px-3 py-2 text-sm text-stone-100">
              <option value="2">2 min</option>
              <option value="3">3 min</option>
              <option value="5">5 min</option>
              <option value="custom">Personnalisé</option>
              <option value="0">Désactivé</option>
            </select>
            {![0, 2, 3, 5].includes(restaurant.ready_alert_minutes ?? 3) && (
              <input data-testid="ready-alert-custom" type="number" min={1} max={30}
                value={restaurant.ready_alert_minutes ?? 4}
                onChange={async (e) => { const v = Math.max(1, Math.min(30, parseInt(e.target.value) || 1)); const r = await api.updateRestaurant(slug, { ready_alert_minutes: v }); onBrandUpdated(r); }}
                className="w-16 bg-[#0A1712] border border-[#1B3028] rounded-xl px-2 py-2 text-sm text-stone-100" />
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium text-stone-100">Escalade « plat prêt » (Manager)</p><p className="text-xs text-stone-500">Canal de l'escalade à la 2e relance</p></div>
          <select data-testid="escalation-channel-select"
            value={restaurant.escalation_channel || "both"}
            onChange={async (e) => { const r = await api.updateRestaurant(slug, { escalation_channel: e.target.value }); onBrandUpdated(r); toast.success("Escalade mise à jour"); }}
            className="bg-[#0A1712] border border-[#1B3028] rounded-xl px-3 py-2 text-sm text-stone-100">
            <option value="both">Push + Son</option>
            <option value="push">Push seulement</option>
            <option value="sound">Son seulement</option>
          </select>
        </div>
      </div>
      <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-4 flex items-center justify-between gap-3">
        <div><p className="text-sm font-medium text-stone-100">Employés & codes PIN</p><p className="text-xs text-stone-500">La gestion du personnel se trouve désormais dans l'onglet « Employés » du menu.</p></div>
        <Button data-testid="settings-go-staff" variant="outline" onClick={() => window.dispatchEvent(new CustomEvent("novanext:go-tab", { detail: "staff" }))} className="rounded-full border-[#1B3028] text-stone-200">Ouvrir</Button>
      </div>

      {/* NOVANEXT POS Connector — architecture prête, aucune caisse réelle connectée */}
      <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-5 space-y-2" data-testid="pos-connector-panel">
        <h3 className="font-serif-display text-lg flex items-center gap-2"><span className="text-[#C69C4A]">🔌</span> NOVANEXT POS Connector</h3>
        <div className="flex items-center gap-2">
          <span data-testid="pos-connector-status" className="text-sm font-semibold text-stone-400">Aucune caisse externe connectée</span>
        </div>
        <p className="text-xs text-stone-500">Ossature prête : NOVANEXT → POS Connector → API caisse externe. <span className="text-stone-300">Premier connecteur cible prévu : une API REST générique (webhooks commandes/paiements), ex. « L'Addition » / « Tiller » / « Zelty ».</span> Aucun logiciel réel n'est connecté ni simulé pour l'instant.</p>
      </div>

      <VatRatesSettings slug={slug} />

      <p className="text-center text-xs text-stone-600">Powered by NOVANEXT</p>

      {/* Danger zone — pilot cleanup */}
      <div className="rounded-2xl bg-[#1C1210] border border-red-500/30 p-5 space-y-3" data-testid="danger-zone">
        <h3 className="font-serif-display text-lg flex items-center gap-2 text-red-300"><AlertTriangle className="w-4 h-4" /> Nettoyage avant lancement</h3>
        <p className="text-xs text-stone-400">Efface toutes les <span className="text-stone-200 font-semibold">commandes, paiements, sessions de table, demandes et avis de démonstration</span> de ce restaurant. Le menu, les produits, la marque, les comptes serveurs et les réglages sont <span className="text-stone-200 font-semibold">conservés</span>. Les numéros de commande repartent à 1. <span className="text-stone-200 font-semibold">Le journal fiscal, les clôtures et les archives ne sont jamais touchés</span> (une écriture RESET est tracée).</p>
        <Button data-testid="reset-activity-open" onClick={() => setShowReset(true)} variant="outline" className="rounded-full bg-transparent border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200">
          <Trash2 className="w-4 h-4 mr-1.5" /> Réinitialiser les données d'activité
        </Button>
      </div>

      <Dialog open={showReset} onOpenChange={(o) => !o && (setShowReset(false), setResetConfirm(""))}>
        <DialogContent data-testid="reset-activity-dialog" className="max-w-md rounded-3xl bg-[#0C1713] border-[#1B3028] text-stone-100">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl flex items-center gap-2 text-red-300"><AlertTriangle className="w-5 h-5" /> Confirmer le nettoyage</DialogTitle>
            <DialogDescription className="text-stone-400">Cette action est irréversible. Tapez <span className="font-mono font-bold text-red-300">REINITIALISER</span> pour confirmer la suppression des données d'activité de « {restaurant.name} ».</DialogDescription>
          </DialogHeader>
          <Input data-testid="reset-confirm-input" value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} placeholder="REINITIALISER" className="bg-[#111F1A] border-[#1B3028] text-stone-100 rounded-xl font-mono" />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => { setShowReset(false); setResetConfirm(""); }} className="rounded-full bg-transparent border-[#1B3028] text-stone-300">Annuler</Button>
            <Button data-testid="reset-confirm-btn" onClick={() => setResetPinOpen(true)} disabled={resetting || resetConfirm.trim() !== "REINITIALISER"} className="rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold disabled:opacity-40">
              {resetting ? "..." : "Tout effacer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PinConfirmDialog open={resetPinOpen} onClose={() => setResetPinOpen(false)} slug={slug}
        title="Confirmer la réinitialisation"
        description="Action critique et irréversible : saisissez le PIN Manager/Propriétaire pour autoriser."
        onConfirmed={doReset} />

      <CreateRestaurantDialog open={showCreateResto} onClose={() => setShowCreateResto(false)} onCreated={(r) => { setShowCreateResto(false); onRestaurantsChanged(r); }} />
    </div>
  );
}

function CreateRestaurantDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", tagline: "", tables_count: 20, brand_primary: "#123026", brand_secondary: "#C69C4A" });
  const [saving, setSaving] = useState(false);
  const create = async () => {
    if (!form.name) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      const r = await api.createRestaurant(form);
      toast.success(`Restaurant « ${r.name} » créé`);
      onCreated(r);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-restaurant-dialog" className="max-w-md rounded-3xl bg-[#FBF9F5]">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#123026]">Nouveau restaurant</DialogTitle>
          <DialogDescription className="sr-only">Créer un restaurant isolé avec son menu et ses tables</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Nom du restaurant"><Input data-testid="new-resto-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white rounded-xl" placeholder="Ex : La Brasserie du Port" /></Field>
          <Field label="Slogan"><Input data-testid="new-resto-tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="bg-white rounded-xl" /></Field>
          <Field label="Nombre de tables"><Input data-testid="new-resto-tables" type="number" min={1} max={100} value={form.tables_count} onChange={(e) => setForm({ ...form, tables_count: parseInt(e.target.value) || 1 })} className="bg-white rounded-xl" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Couleur principale"><input type="color" value={form.brand_primary} onChange={(e) => setForm({ ...form, brand_primary: e.target.value })} className="w-full h-10 rounded-lg border border-[#EAE5DC] cursor-pointer" /></Field>
            <Field label="Couleur secondaire"><input type="color" value={form.brand_secondary} onChange={(e) => setForm({ ...form, brand_secondary: e.target.value })} className="w-full h-10 rounded-lg border border-[#EAE5DC] cursor-pointer" /></Field>
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-full">Annuler</Button>
          <Button data-testid="new-resto-create" onClick={create} disabled={saving} className="flex-1 rounded-full bg-[#123026] hover:bg-[#0A1C16] text-white">{saving ? "..." : "Créer"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
